const IMAGE_QUALITY = 0.95;
// image utility functions for resizing and optimization

interface ResizeOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0-1 for JPEG quality
  format?: "jpeg" | "png" | "webp";
}

interface ImageDimensions {
  width: number;
  height: number;
}

// calculate new dimensions while maintaining aspect ratio
export function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth?: number,
  maxHeight?: number,
): ImageDimensions {
  let { width, height } = { width: originalWidth, height: originalHeight };

  if (maxWidth && width > maxWidth) {
    height = (height * maxWidth) / width;
    width = maxWidth;
  }

  if (maxHeight && height > maxHeight) {
    width = (width * maxHeight) / height;
    height = maxHeight;
  }

  return { width: Math.round(width), height: Math.round(height) };
}

// resize image file using canvas
export function resizeImage(
  file: File,
  options: ResizeOptions = {},
): Promise<File> {
  return new Promise((resolve, reject) => {
    const {
      maxWidth = 1920,
      maxHeight = 1080,
      quality = IMAGE_QUALITY,
      format = "jpeg",
    } = options;

    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("failed to get canvas context"));
      return;
    }

    img.onload = () => {
      const { width, height } = calculateDimensions(
        img.width,
        img.height,
        maxWidth,
        maxHeight,
      );

      canvas.width = width;
      canvas.height = height;

      // draw image on canvas with new dimensions
      ctx.drawImage(img, 0, 0, width, height);

      // convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("failed to create blob from canvas"));
            return;
          }

          // create new file with resized image
          const resizedFile = new File([blob], file.name, {
            type: `image/${format}`,
            lastModified: Date.now(),
          });

          resolve(resizedFile);
        },
        `image/${format}`,
        quality,
      );
    };

    img.onerror = () => {
      reject(new Error("failed to load image"));
    };

    img.src = URL.createObjectURL(file);
  });
}

// create thumbnail from image file
export function createThumbnail(file: File, size: number = 200): Promise<File> {
  return resizeImage(file, {
    maxWidth: size,
    maxHeight: size,
    quality: IMAGE_QUALITY,
    format: "jpeg",
  });
}

// validate image file
export function validateImageFile(
  file: File,
  options: {
    maxSize?: number;
    allowedTypes?: string[];
    minWidth?: number;
    minHeight?: number;
    maxWidth?: number;
    maxHeight?: number;
  } = {},
): Promise<{ isValid: boolean; error?: string; dimensions?: ImageDimensions }> {
  return new Promise((resolve) => {
    const {
      maxSize = 10 * 1024 * 1024, // 10MB
      allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"],
      minWidth = 100,
      minHeight = 100,
      maxWidth = 5000,
      maxHeight = 5000,
    } = options;

    // check file type
    if (!allowedTypes.includes(file.type)) {
      resolve({
        isValid: false,
        error: `file type ${file.type} is not allowed`,
      });
      return;
    }

    // check file size
    if (file.size > maxSize) {
      resolve({
        isValid: false,
        error: `file size ${Math.round(file.size / 1024 / 1024)}MB exceeds limit of ${Math.round(maxSize / 1024 / 1024)}MB`,
      });
      return;
    }

    // check image dimensions
    const img = new Image();
    img.onload = () => {
      const dimensions = { width: img.width, height: img.height };

      if (img.width < minWidth || img.height < minHeight) {
        resolve({
          isValid: false,
          error: `image dimensions ${img.width}x${img.height} are too small (minimum: ${minWidth}x${minHeight})`,
          dimensions,
        });
        return;
      }

      if (img.width > maxWidth || img.height > maxHeight) {
        resolve({
          isValid: false,
          error: `image dimensions ${img.width}x${img.height} are too large (maximum: ${maxWidth}x${maxHeight})`,
          dimensions,
        });
        return;
      }

      resolve({ isValid: true, dimensions });
    };

    img.onerror = () => {
      resolve({
        isValid: false,
        error: "invalid image file",
      });
    };

    img.src = URL.createObjectURL(file);
  });
}

// get image metadata
export function getImageMetadata(file: File): Promise<{
  dimensions: ImageDimensions;
  size: number;
  type: string;
  name: string;
}> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve({
        dimensions: { width: img.width, height: img.height },
        size: file.size,
        type: file.type,
        name: file.name,
      });
    };

    img.onerror = () => {
      reject(new Error("failed to load image metadata"));
    };

    img.src = URL.createObjectURL(file);
  });
}

// convert image to different format
export function convertImageFormat(
  file: File,
  targetFormat: "jpeg" | "png" | "webp",
  quality: number = IMAGE_QUALITY,
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("failed to get canvas context"));
      return;
    }

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;

      // fill with white background for jpeg
      if (targetFormat === "jpeg") {
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.drawImage(img, 0, 0);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("failed to convert image"));
            return;
          }

          const convertedFile = new File([blob], file.name, {
            type: `image/${targetFormat}`,
            lastModified: Date.now(),
          });

          resolve(convertedFile);
        },
        `image/${targetFormat}`,
        quality,
      );
    };

    img.onerror = () => {
      reject(new Error("failed to load image for conversion"));
    };

    img.src = URL.createObjectURL(file);
  });
}

// compress image file
export function compressImage(
  file: File,
  compressionRatio: number = IMAGE_QUALITY,
): Promise<File> {
  return resizeImage(file, {
    quality: compressionRatio,
    format: file.type.includes("png") ? "png" : "jpeg",
  });
}

// generate multiple sizes of an image
export async function generateImageSizes(
  file: File,
  sizes: { name: string; width: number; height?: number }[],
): Promise<{ [key: string]: File }> {
  const results: { [key: string]: File } = {};

  for (const size of sizes) {
    try {
      const resizedFile = await resizeImage(file, {
        maxWidth: size.width,
        maxHeight: size.height || size.width,
        quality: IMAGE_QUALITY,
        format: "jpeg",
      });
      results[size.name] = resizedFile;
    } catch (error) {
      console.error(`failed to generate ${size.name} size:`, error);
    }
  }

  return results;
}

// batch process images
export async function batchProcessImages(
  files: File[],
  processor: (file: File) => Promise<File>,
): Promise<File[]> {
  const processed: File[] = [];

  for (const file of files) {
    try {
      const processedFile = await processor(file);
      processed.push(processedFile);
    } catch (error) {
      console.error(`failed to process ${file.name}:`, error);
      // keep original file if processing fails
      processed.push(file);
    }
  }

  return processed;
}
